import useTheme from '@material-ui/core/styles/useTheme';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import styled from 'styled-components';

import { useAuth } from 'src/app/infra/auth';
import { useChats } from 'src/app/infra/chats';
import { useEvents } from 'src/app/infra/events';
import { useRoute } from 'src/app/infra/routes';
import { getEntrada, getVotacao } from 'src/app/services/votacao';
import { Loading, Msgbox, Optional } from 'src/app/shared/utils';
import {
  Entrada,
  ETipoVideo,
  Pergunta,
  TipoExibicaoChat,
  VotacaoItem
} from 'src/app/types/votacao';
import { useWrapper } from 'src/utils/axios';
import Header from './patterns/header';
import Voting from './patterns/voting';

const Video = lazy(() => import('./patterns/video'));
const Presentation = lazy(() => import('./patterns/presentation'));

const View = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: auto;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: 1rem;
  }
`;

const StyledTab = styled(Tab)`
  font-weight: bold;
`;

const ButtonFloat = styled.a`
  position: fixed;
  width: 250px;
  height: 30px;
  bottom: 40px;
  padding-top: 5px;
  right: 40px;
  background-color: #3cb5a9;
  color: #fff;
  border-radius: 5px;
  text-align: center;
  font-size: 30px;
  box-shadow: 1px 1px 2px #888;
  z-index: 1000;
`;

const TextFloatButton = styled.h5`
  font-size: 13px;
`;

export default () => {
  const {
    params: { token, condominio },
    query: { redirect }
  } = useRoute();
  const events = useEvents();
  const { session, authOff } = useAuth();
  const {
    mountBlipChat,
    mountMovideskChat,
    destroyBlipChat,
    destroyMovideskChat
  } = useChats();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const response = useWrapper<VotacaoItem>();
  const responseEntrada = useWrapper<Entrada>();
  const [tabSelected, setTabSelected] = useState(0);
  const [hasQuestionOpen, setHasQuestionOpen] = useState(false);

  const hasVideo = !!response.payload?.tipoVideo;
  const hasPresentation = !!response.payload?.urlDoctoVotacao;
  const hasCodCond = response.payload?.condominio.codigoCondominio;

  const validaAntesDeLiberar = (codVotacao: number) => {
    responseEntrada.exec(
      getEntrada(codVotacao),
      (data) => {
        if (!data.assinatura.codigo) {
          authOff();
        }
      },
      () => setTimeout(() => authOff(), 2000)
    );

    return () => responseEntrada.cancel();
  };

  const refresh = () => {
    response.exec(
      getVotacao(token),
      (resp) => {
        if (!!resp.tipoVideo) {
          setTabSelected(1);
        } else if (!!resp.urlDoctoVotacao) {
          setTabSelected(2);
        }

        if (session?.user?.unidadeLogin?.tipoUsuario !== 4) {
          validaAntesDeLiberar(resp.codigoVotacao);
        }
      },
      () => setTimeout(() => authOff(), 2000),
      { defaultErrorMsg: 'Erro ao buscar as informações da votação!' }
    );
  };

  function validateIfVotingDone(v: Pergunta[]) {
    const has = v?.some((x) => !x.votoUsuario);
    setHasQuestionOpen(has);
  }

  // const removeButton = () => {
  //   var appBanners = document.getElementsByClassName('inner-2nxQ')  as HTMLCollectionOf<HTMLElement>;
  //   for (var i = 0; i < appBanners.length; i ++) {
  //     appBanners[i].style.display = 'none';
  // }
  // document.getElementsByClassName('inner-2nxQ')[0].style.visibility = 'hidden';
  // document.querySelectorAll(".<className>");
  // }

  useEffect(() => {
    // ! bloqueia msm sessao para mais de um condominio (acesso multi tabs)
    if (session?.condominio !== condominio) {
      authOff();
      return;
    }

    setTimeout(() => {
      // ! força redirect caso o usuario tente voltar para home com parametro redirect
      // if (redirect) {
      //   events.stop();
      //   window.location.href = redirect;
      //   return;
      // }

      refresh();
    }, 300);
    return () => response.cancel();
  }, []);

  useEffect(() => {
    if (isMobile && !!!response.payload?.ocultarPergunta) {
      setTabSelected(0);
    } else if (hasVideo) {
      setTabSelected(1);
      // setTimeout(function(){ removeButton(); }, 1000000000);
    } else if (hasPresentation) {
      setTabSelected(2);
    }
  }, [isMobile]);

  useEffect(() => {
    if (response.payload?.tipoExibicaoChat) {
      switch (response.payload?.tipoExibicaoChat) {
        case TipoExibicaoChat.BlipChat:
          mountBlipChat();
          break;
        case TipoExibicaoChat.MovideskChat:
          mountMovideskChat();
          break;
        case TipoExibicaoChat.BlipChatApenasAreaDeslogada:
          destroyBlipChat();
          break;
        case TipoExibicaoChat.MovideskChatApenasAreaDeslogada:
          destroyMovideskChat();
          break;
      }
    }
  }, [response.payload]);

  return (
    <>
      <Optional valid={!!response.payload}>
        <Header data={response.payload} canExit={!hasQuestionOpen} />
      </Optional>
      <View>
        <Optional valid={!!response.payload}>
          <Optional valid={!isMobile && !!!response.payload?.ocultarPergunta}>
            <Column>
              <Voting
                data={response.payload}
                onRefresh={validateIfVotingDone}
              />
            </Column>
          </Optional>
          <Column
            style={
              hasVideo || hasPresentation
                ? { flex: 1.2 }
                : { width: 'unset', flex: 'unset' }
            }
          >
            <Tabs
              value={tabSelected}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              TabIndicatorProps={{
                style: {
                  backgroundColor:
                    response.payload?.identidadeVisual?.headerColor || '#083F8B'
                }
              }}
              onChange={(e, i) => setTabSelected(i)}
            >
              <StyledTab
                label="Votação"
                style={{
                  display:
                    isMobile && !!!response.payload?.ocultarPergunta
                      ? 'block'
                      : 'none',
                  color:
                    (response.payload?.identidadeVisual?.fontColor ?? '#000') <
                    '#d3d3d3'
                      ? response.payload?.identidadeVisual?.fontColor
                      : '#000'
                }}
              />
              <StyledTab
                label={
                  response.payload?.tipoVideo === ETipoVideo.Zoom ||
                  response.payload?.tipoVideo === ETipoVideo.LiveWebinar
                    ? 'Ao vivo'
                    : 'Vídeo'
                }
                style={{
                  display: hasVideo ? 'block' : 'none',
                  color:
                    (response.payload?.identidadeVisual?.fontColor ?? '#000') <
                    '#d3d3d3'
                      ? response.payload?.identidadeVisual?.fontColor
                      : '#000'
                }}
              />
              <StyledTab
                label="Apresentação"
                style={{
                  display: hasPresentation ? 'block' : 'none',
                  color:
                    (response.payload?.identidadeVisual?.fontColor ?? '#000') <
                    '#d3d3d3'
                      ? response.payload?.identidadeVisual?.fontColor
                      : '#000'
                }}
              />
              <ButtonFloat
                style={{ display: hasCodCond === 2646 ? 'block' : 'none' }}
                href="http://online.anyflip.com/tihx/iedk/mobile/index.html"
                target="_blank"
              >
                <TextFloatButton>
                  Acessar a Previsão Orçamentária
                </TextFloatButton>
              </ButtonFloat>
              <ButtonFloat
                style={{ display: hasCodCond === 2647 ? 'block' : 'none' }}
                href="http://online.anyflip.com/tihx/qpkx/mobile/index.html"
                target="_blank"
              >
                <TextFloatButton>
                  Acessar a Previsão Orçamentária
                </TextFloatButton>
              </ButtonFloat>
            </Tabs>
            <SwipeableViews
              index={tabSelected}
              onChangeIndex={(i) => setTabSelected(i)}
            >
              <Optional valid={isMobile}>
                <Voting
                  data={response.payload}
                  onRefresh={validateIfVotingDone}
                />
              </Optional>
              <Optional valid={hasVideo}>
                <Suspense fallback={<div>Carregando...</div>}>
                  {response.payload && (
                    <Video
                      type={response.payload.tipoVideo}
                      link={response.payload.linkVideo}
                      videoClientId={response.payload.videoClientId}
                      videoToken={response.payload.videoToken}
                    />
                  )}
                </Suspense>
              </Optional>
              <Optional valid={hasPresentation}>
                <Suspense fallback={<div>Carregando...</div>}>
                  <Presentation
                    urlDocument={response.payload?.urlDoctoVotacao}
                  />
                </Suspense>
              </Optional>
            </SwipeableViews>
            {/* {isMobile ? (<MobileTour />) : (<Tour linkAjuda={!!response.payload?.identidadeVisual ? response.payload?.identidadeVisual.linkAjuda : "http://conteudo.hubert.com.br/"} />)} */}
          </Column>
        </Optional>
      </View>
      <Loading open={response.loading} hasBackdrop />
      <Msgbox error={response.error} onClose={() => response.reset()} />
    </>
  );
};
